<template>
  <div class="markdown-menu d-flex align-center flex-wrap">
    <template v-for="(item, index) in menuItems">
      <v-divider
        v-if="item.type == 'divider'"
        :key="index"
        vertical
        class="py-2 mx-2"
      />
      <v-btn
        v-else
        :key="index"
        icon
        :title="item.title"
        :disabled="item.disabled"
        @click="item.action"
      >
        <v-icon
          :class="{ 'is-active': item.isActive ? item.isActive() : null }"
        >
          {{ item.icon }}
        </v-icon>
      </v-btn>
    </template>
    <label for="files" class="btn"> <v-icon>image</v-icon></label>
    <input
      id="files"
      style="visibility:hidden; position: absolute; width: 24px; height: 24px"
      type="file"
      title="Зображення"
      multiple
      @input="handleImageUpload"
    />
    <v-menu offset-y :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon v-bind="attrs" title="Таблиця" v-on="on">
          <v-icon>mdi-table</v-icon>
        </v-btn>
      </template>
      <v-list dense class="pt-0 pb-0">
        <v-list-item v-for="(item, i) in tableMenu" :key="i">
          <v-list-item-title style="cursor: pointer;" @click="item.action">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <input
      type="color"
      :value="editor.getAttributes('textStyle').color"
      style="width: 24px; height: 24px"
      title="Палітра кольорів"
      @input="
        editor
          .chain()
          .focus()
          .setColor($event.target.value)
          .run()
      "
    />
    <slot name="expand-button" />
  </div>
</template>

<script>
import upload from "../../helpers/uploadImage.js";
import { uploadImage } from "../../backend/uploadApi.js";

export default {
  props: {
    editor: {
      type: Object,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      menuItems: [
        {
          icon: "mdi-format-header-1",
          title: "Заголовок 1",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleHeading({ level: 1 })
              .run(),
          isActive: () => this.editor.isActive("heading", { level: 1 }),
        },
        {
          icon: "mdi-format-header-2",
          title: "Заголовок 2",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleHeading({ level: 2 })
              .run(),
          isActive: () => this.editor.isActive("heading", { level: 2 }),
        },
        {
          icon: "mdi-format-paragraph",
          title: "Абзац",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setParagraph()
              .run(),
          isActive: () => this.editor.isActive("paragraph"),
        },
        {
          icon: "format_bold",
          title: "Жирний",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleBold()
              .run(),
          isActive: () => this.editor.isActive("bold"),
        },
        {
          icon: "format_italic",
          title: "Курсив",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleItalic()
              .run(),
          isActive: () => this.editor.isActive("italic"),
        },
        {
          icon: "strikethrough_s",
          title: "Закреслений",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleStrike()
              .run(),
          isActive: () => this.editor.isActive("strike"),
        },
        {
          icon: "code",
          title: "Code",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleCode()
              .run(),
          isActive: () => this.editor.isActive("code"),
        },
        {
          icon: "mdi-format-color-highlight",
          title: "Виділити",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleHighlight()
              .run(),
          isActive: () => this.editor.isActive("highlight"),
        },
        {
          type: "divider",
        },
        {
          icon: "mdi-format-align-left",
          title: "Вирівняти по лівому краю",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setTextAlign("left")
              .unsetAllMarks()
              .run(),
          isActive: () => this.editor.isActive({ textAlign: "left" }),
        },
        {
          icon: "mdi-format-align-center",
          title: "Вирівняти по центру",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setTextAlign("center")
              .unsetAllMarks()
              .run(),
          isActive: () => this.editor.isActive({ textAlign: "center" }),
        },
        {
          icon: "mdi-format-align-right",
          title: "Вирівняти по правому краю",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setTextAlign("right")
              .unsetAllMarks()
              .run(),
          isActive: () => this.editor.isActive({ textAlign: "right" }),
        },
        {
          icon: "mdi-format-align-justify",
          title: "Вирівняти по ширині",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setTextAlign("justify")
              .unsetAllMarks()
              .run(),
          isActive: () => this.editor.isActive({ textAlign: "justify" }),
        },
        { type: "divider" },
        {
          icon: "format_list_bulleted",
          title: "Маркерний список",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleBulletList()
              .run(),
          isActive: () => this.editor.isActive("bulletList"),
        },
        {
          icon: "mdi-order-numeric-ascending",
          title: "Упорядкований список",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleOrderedList()
              .run(),
          isActive: () => this.editor.isActive("orderedList"),
        },
        {
          icon: "mdi-format-list-checks",
          title: "Список завдань",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleTaskList()
              .run(),
          isActive: () => this.editor.isActive("taskList"),
        },
        {
          type: "divider",
        },
        {
          icon: "mdi-code-not-equal-variant",
          title: "Фрагмент коду",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleCodeBlock()
              .run(),
          isActive: () => this.editor.isActive("codeBlock"),
        },
        {
          icon: "mdi-format-quote-open",
          title: "Цитата",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleBlockquote()
              .run(),
          isActive: () => this.editor.isActive("blockquote"),
        },
        {
          type: "divider",
        },
        {
          icon: "mdi-wrap",
          title: "Перейти на новий рядок",
          action: () =>
            this.editor
              .chain()
              .focus()
              .setHardBreak()
              .run(),
        },
        {
          icon: "mdi-format-clear",
          title: "Очистити форматування",
          action: () =>
            this.editor
              .chain()
              .focus()
              .clearNodes()
              .unsetAllMarks()
              .run(),
        },
        {
          type: "divider",
        },
        {
          icon: "link",
          title: "Посилання",
          action: this.setLink,
          isActive: () => this.editor.isActive("link"),
        },
      ],
      tableMenu: [
        {
          title: "Додати таблицю",
          action: () =>
            this.editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run(),
        },
        {
          title: "Видалити таблицю",
          action: () =>
            this.editor
              .chain()
              .focus()
              .deleteTable()
              .run(),
        },
        {
          title: "Додати колонку до",
          action: () =>
            this.editor
              .chain()
              .focus()
              .addColumnBefore()
              .run(),
        },
        {
          title: "Додати колонку після",
          action: () =>
            this.editor
              .chain()
              .focus()
              .addColumnAfter()
              .run(),
        },
        {
          title: "Видалити колонку",
          action: () =>
            this.editor
              .chain()
              .focus()
              .deleteColumn()
              .run(),
        },
        {
          title: "Додати рядок до",
          action: () =>
            this.editor
              .chain()
              .focus()
              .addRowBefore()
              .run(),
        },
        {
          title: "Додати рядок після",
          action: () =>
            this.editor
              .chain()
              .focus()
              .addRowAfter()
              .run(),
        },
        {
          title: "Видалити рядок",
          action: () =>
            this.editor
              .chain()
              .focus()
              .deleteRow()
              .run(),
        },
        {
          title: "Об'єднати колонки",
          action: () =>
            this.editor
              .chain()
              .focus()
              .mergeCells()
              .run(),
        },
        {
          title: "Роз'єднати колонки",
          action: () =>
            this.editor
              .chain()
              .focus()
              .splitCell()
              .run(),
        },
        {
          title: "Виділити заголовок таблиці",
          action: () =>
            this.editor
              .chain()
              .focus()
              .toggleHeaderCell()
              .run(),
        },
      ],
    };
  },
  computed: {
    baseURL() {
      const baseURL = process.env.VUE_APP_API_URL;
      return baseURL.substr(0, baseURL.indexOf("api"));
    },
  },
  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

      if (url === null) {
        return;
      }

      if (url === "") {
        this.editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .unsetLink()
          .run();

        return;
      }

      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    },
    async handleImageUpload(event) {
      const formData = upload(event);
      const images = await uploadImage(formData);

      images.forEach((image) => {
        this.editor
          .chain()
          .focus()
          .setImage({
            src: `${this.baseURL}/images/${image.uploadedImageName}`,
          })
          .run();
      });
    },
  },
};
</script>

<style lang="scss">
.content-menu {
  border: 1px solid #ccc;
  box-sizing: border-box;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  padding: 8px;
}
.content-menu .v-icon {
  &.is-active,
  &:hover {
    color: #e57373;
  }
}
</style>
